import { isMenuOpen, isTypingIn, KeyBinding, matches } from './keys';

/**
 * The one place keyboard shortcuts are handled.
 *
 * One `keydown` listener on `document`, in the capture phase - the player
 * lives in the footer and nothing in a music library is focused on purpose,
 * so shortcuts cannot hang off focus - and a list of what is registered.
 * Components register through `useShortcut`; the overlay reads the same
 * list, so what it shows is what works, never a hand-maintained copy.
 *
 * Who gets a key when several want it: the highest `priority` first, and
 * among equals the most recently registered. A dialog mounts on top of a
 * page, so it registers later and wins Escape; a page's shortcuts are back
 * as soon as it unmounts. A handler may decline by returning false, and the
 * key goes on to the next one and finally to the browser - space with
 * nothing to play still scrolls the page.
 *
 * What the browser and other libraries already handled is left alone: an
 * event with `defaultPrevented` set is not looked at, and while a
 * react-contextmenu menu is open (it takes Enter, Escape and the arrows
 * itself) only shortcuts that ask for it run. Text boxes keep every key
 * without a modifier unless a shortcut says otherwise.
 *
 * Chords: a binding with a `prefix` fires when its key follows the prefix
 * key within `chordWindowMs`. The prefix alone is swallowed and shown as
 * pending, so the overlay can say "g …".
 */

export type ShortcutGroup = 'Playback' | 'Navigation' | 'Library' | 'Dialogs' | 'Help';

export interface Shortcut {
  /**
   * Names the action, not the owner. Two views registering 'select-all'
   * are one line on the overlay and the newer one gets the key.
   */
  id: string;
  keys: KeyBinding | KeyBinding[];
  group: ShortcutGroup;
  /** As the overlay shows it: "Play or pause". */
  description: string;
  /** Does the action. Return false to decline the key. */
  run(ev: KeyboardEvent): boolean | void;
  /** Fires inside text boxes too. Default false. */
  whileTyping?: boolean;
  /** Fires on key repeat too. Default false. */
  repeat?: boolean;
  /** Fires while a context menu is open. Default false. */
  whileMenuOpen?: boolean;
  /** Higher goes first regardless of registration order. Default 0. */
  priority?: number;
  /** Shown on the overlay. Default true. */
  listed?: boolean;
}

export const chordWindowMs = 1500;

interface Entry {
  seq: number;
  get(): Shortcut;
}

/**
 * A key a shortcut handled goes no further: not to the browser and not to
 * whatever element it was aimed at - a focused button, say, which would
 * otherwise act on the same space. That is why the registry listens in the
 * capture phase, on document: it has to see the key before the element does.
 *
 * It does not settle the native audio controls, and nothing at document
 * level can. In Chromium a key pressed while the focus is *inside* those
 * controls - on the timeline after a click on it - never leaves the shadow
 * tree; not even a capture listener on window sees it (measured in Edge,
 * 2026-09-27). The player keeps focus off the audio element instead.
 */
function take(ev: KeyboardEvent) {
  ev.preventDefault();
  ev.stopPropagation();
}

export function bindingsOf(shortcut: Shortcut): KeyBinding[] {
  return Array.isArray(shortcut.keys) ? shortcut.keys : [shortcut.keys];
}

export class ShortcutRegistry {
  private entries: Entry[] = [];
  private seq = 0;
  private listeners = new Set<() => void>();
  private pending: { prefix: string; at: number } | undefined;
  private pendingTimer: ReturnType<typeof setTimeout> | undefined;

  /** Replaceable so a test can move the clock. */
  now: () => number = () => Date.now();

  /**
   * Registers a shortcut, read through `get` on every key so the latest
   * closure is used without re-registering - and without losing the place
   * in the order that mount time gave it.
   */
  register(get: () => Shortcut) {
    const entry: Entry = { seq: ++this.seq, get };

    if (this.entries.length === 0) {
      document.addEventListener('keydown', this.handleKeyDown, true);
    }

    this.entries.push(entry);
    this.notify();

    return () => {
      this.entries = this.entries.filter((x) => x !== entry);

      if (this.entries.length === 0) {
        document.removeEventListener('keydown', this.handleKeyDown, true);
      }

      this.notify();
    };
  }

  /** Everything registered, in dispatch order, one per id. */
  list(): Shortcut[] {
    const seen = new Set<string>();
    const result: Shortcut[] = [];

    for (const entry of this.ordered()) {
      const shortcut = entry.get();

      if (shortcut.listed !== false && !seen.has(shortcut.id)) {
        seen.add(shortcut.id);
        result.push(shortcut);
      }
    }

    return result;
  }

  /** The chord prefix waiting for its second key, if any. */
  pendingPrefix() {
    return this.pending?.prefix;
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((fn) => fn());
  }

  private ordered() {
    return this.entries
      .map((entry) => ({ entry, priority: entry.get().priority || 0 }))
      .sort((a, b) => b.priority - a.priority || b.entry.seq - a.entry.seq)
      .map((x) => x.entry);
  }

  private setPending(pending: { prefix: string; at: number } | undefined) {
    this.pending = pending;

    if (this.pendingTimer) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = undefined;
    }

    if (pending) {
      this.pendingTimer = setTimeout(() => this.setPending(undefined), chordWindowMs);
    }

    this.notify();
  }

  private allowed(shortcut: Shortcut, ev: KeyboardEvent) {
    if (ev.repeat && !shortcut.repeat) {
      return false;
    }

    if (!shortcut.whileTyping && isTypingIn(ev)) {
      return false;
    }

    if (!shortcut.whileMenuOpen && isMenuOpen()) {
      return false;
    }

    return true;
  }

  /** Runs the first taker. True when one took the key. */
  private dispatch(ev: KeyboardEvent, prefix: string | undefined) {
    for (const entry of this.ordered()) {
      const shortcut = entry.get();

      const binding = bindingsOf(shortcut).find((b) => b.prefix === prefix && matches(b, ev));

      if (!binding || !this.allowed(shortcut, ev)) {
        continue;
      }

      if (shortcut.run(ev) !== false) {
        take(ev);

        return true;
      }
    }

    return false;
  }

  private startsChord(ev: KeyboardEvent) {
    if (ev.repeat || ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey || ev.key.length !== 1) {
      return false;
    }

    if (isTypingIn(ev) || isMenuOpen()) {
      return false;
    }

    return this.entries.some((entry) => bindingsOf(entry.get()).some((b) => b.prefix === ev.key));
  }

  handleKeyDown = (ev: KeyboardEvent) => {
    if (ev.defaultPrevented) {
      return;
    }

    const pending = this.pending;

    if (pending) {
      this.setPending(undefined);

      if (this.now() - pending.at < chordWindowMs && this.dispatch(ev, pending.prefix)) {
        return;
      }

      // A prefix followed by a key that means nothing with it: the key is
      // treated as if the prefix had never been pressed.
    }

    if (this.startsChord(ev)) {
      this.setPending({ prefix: ev.key, at: this.now() });
      take(ev);

      return;
    }

    this.dispatch(ev, undefined);
  };
}

const registry = new ShortcutRegistry();

export default registry;
